/**
 * aiExamRegenerationService.ts
 * Centralizes logic for determining when a new AI Exam should be generated.
 * Prevents cost spam while maintaining exam quality. Learning gates: 5 practice tests before first AI exam; 3 focus areas >= 80% for next.
 */

import { logger } from '../utils/logger';
import { aiCoach } from './aiCoach';

export interface LastAIExamData {
  date: Date;
  questions: any[];
  examId: string;
}

class AIExamRegenerationService {
  private readonly STORAGE_KEY = 'lastAIExamData';
  private readonly REQUIRED_FOCUS_SCORE = 80;
  private readonly PREVIEW_GATE_TESTS = 5;

  /**
   * Get last AI Exam date from localStorage
   */
  getLastAIExamDate(): Date | null {
    try {
      const lastExamData = this.getLastExamData();
      return lastExamData ? new Date(lastExamData.date) : null;
    } catch (error) {
      logger.error('Error getting last AI Exam date:', error);
      return null;
    }
  }

  /**
   * Get last AI Exam data (questions, date)
   */
  getLastExamData(): LastAIExamData | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      
      const data = JSON.parse(stored);
      return {
        date: new Date(data.date),
        questions: data.questions || [],
        examId: data.examId || 'ai-exam'
      };
    } catch (error) {
      logger.error('Error getting last AI Exam data:', error);
      return null;
    }
  }

  /**
   * Save last AI Exam data to localStorage
   */
  saveLastExamData(questions: any[], examId: string = 'ai-exam'): void {
    try {
      const data: LastAIExamData = {
        date: new Date(),
        questions: questions,
        examId: examId
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      logger.debug('Saved last AI Exam data');
    } catch (error) {
      logger.error('Error saving last AI Exam data:', error);
    }
  }

  /**
   * Single rule with preview:
   * - Before first AI exam: require 5 practice tests (preview gate).
   * - After first AI exam: require current 3 focus areas each >= 80%.
   */
  hasSufficientNewLearning(): boolean {
    if (!this.hasTakenAIExam()) {
      return this.hasPassedPreviewGate();
    }
    return this.meetsFocusRule();
  }

  private hasPassedPreviewGate(): boolean {
    const testHistory = aiCoach.getTestHistory();
    const practiceTestCount = testHistory.filter(test =>
      test.testId !== 'mock-exam' &&
      test.testId !== 'ai-exam' &&
      !test.testId.includes('mock')
    ).length;
    return practiceTestCount >= this.PREVIEW_GATE_TESTS;
  }

  private hasTakenAIExam(): boolean {
    const testHistory = aiCoach.getTestHistory();
    if (testHistory.some(test => test.testId === 'ai-exam')) return true;
    try {
      const { getExamHistory } = require('./aiExamUnlockService');
      return getExamHistory().length > 0;
    } catch {
      return false;
    }
  }

  private meetsFocusRule(): boolean {
    const focusAreas = this.getCurrentFocusAreas();
    if (focusAreas.length < 3) {
      return false;
    }
    const meetsRule = focusAreas.every(area => area.lastScore >= this.REQUIRED_FOCUS_SCORE);
    logger.debug('AI Exam focus rule check:', {
      focusAreas: focusAreas.map(area => ({
        testId: area.testId,
        lastScore: area.lastScore
      })),
      meetsRule
    });
    return meetsRule;
  }

  /**
   * Check if new AI Exam should be generated
   * Requires sufficient new learning
   */
  canRegenerate(): boolean {
    const ruleA = this.hasSufficientNewLearning();
    logger.debug('AI Exam regeneration check:', { ruleA });
    return ruleA;
  }

  private getCurrentFocusAreas(): Array<{ testId: string; lastScore: number }> {
    const scores = aiCoach.getTestScores();
    const rankedByWeakness = Object.keys(scores)
      .map(testId => ({
        testId,
        average: scores[testId]?.average ?? 0,
        lastScore: scores[testId]?.lastScore ?? 0,
        count: scores[testId]?.count ?? 0
      }))
      .filter(entry => entry.count > 0)
      .sort((a, b) => a.average - b.average)
      .slice(0, 3);

    return rankedByWeakness.map(entry => ({
      testId: entry.testId,
      lastScore: entry.lastScore
    }));
  }

  /**
   * Restore last exam questions
   * Returns null if no saved exam exists
   */
  restoreLastExam(): any[] | null {
    const lastExamData = this.getLastExamData();
    if (!lastExamData || !lastExamData.questions || lastExamData.questions.length === 0) {
      return null;
    }

    logger.debug('Restoring last AI Exam questions:', lastExamData.questions.length);
    return lastExamData.questions;
  }

  /**
   * Clear last exam data (e.g., when user explicitly wants new exam)
   */
  clearLastExamData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      logger.debug('Cleared last AI Exam data');
    } catch (error) {
      logger.error('Error clearing last AI Exam data:', error);
    }
  }
}

export const aiExamRegenerationService = new AIExamRegenerationService();
